import * as Slider from "@radix-ui/react-slider";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useAppProperties } from "@/hooks";
import { useTranslate } from "@/providers/context";
import { DURATION_PARAM } from "../const";

const DEFAULT_MIN = 0;
const DEFAULT_MAX = 90;
const DEFAULT_INCREMENT = 5;

interface SliderProperty {
  Min: number;
  Max: number;
  Increment: number;
}

export const DurationSlider = () => {
  const { translate } = useTranslate();
  const { data: properties } = useAppProperties();
  const [searchParams, setSearchParams] = useSearchParams();

  const durationParams = useMemo(
    () => new URLSearchParams(searchParams),
    [searchParams],
  );

  const sliderProperty: SliderProperty | undefined =
    properties?.FilterScreenDurationSliderSettings
      ? JSON.parse(properties?.FilterScreenDurationSliderSettings)
      : undefined;

  const min = sliderProperty?.Min ?? DEFAULT_MIN;
  const max = sliderProperty?.Max ?? DEFAULT_MAX;
  const step = sliderProperty?.Increment ?? DEFAULT_INCREMENT;

  const initialParam = durationParams.get(DURATION_PARAM)?.split(",");
  const initialValue = initialParam
    ? [parseInt(initialParam[0]), parseInt(initialParam[1])]
    : undefined;

  const [value, setValue] = useState<number[] | undefined>(initialValue);

  const setDurationFilter = (value: number[]) => {
    durationParams.set(DURATION_PARAM, value.join(","));
    setSearchParams(durationParams);
  };

  // Keep the slider value in sync with the query param if changed externally
  useEffect(() => {
    const param = durationParams.get(DURATION_PARAM)?.split(",");
    if (param) {
      setValue([parseInt(param[0]), parseInt(param[1])]);
    } else {
      setValue([min, max]); // Reset to default values when no param is present
    }
  }, [searchParams, min, max, durationParams]);

  return (
    <div className="mb-[32px]">
      <div className="mb-[12px] flex w-full justify-between">
        <div className="p2-bold">{translate("Duration")}</div>
        <div className="p2-medium text-item-secondary">{`${value ? value[0] : min} - ${value ? value[1] : max}`}</div>
      </div>
      <Slider.Root
        className="relative flex h-[20px] w-full touch-none select-none items-center"
        value={value}
        min={min}
        max={max}
        defaultValue={[min, max]}
        step={step}
        onValueChange={setValue}
        onValueCommit={setDurationFilter}
      >
        <Slider.Track className="bg-surface-tertiary relative h-[6px] grow rounded-full">
          <Slider.Range className="absolute h-full rounded-full bg-highlightPrimary" />
        </Slider.Track>
        {Array(2)
          .fill("any")
          .map((_, idx) => (
            <Slider.Thumb
              key={idx}
              className="hover:bg-white-hover block h-[16px] w-[16px] cursor-pointer rounded-full bg-white drop-shadow-default focus:outline-none"
            />
          ))}
      </Slider.Root>
    </div>
  );
};
